/**
 * A/B Testing Types
 * Story: E007-S011 - A/B Test Manager
 */

export interface ABTestVariant {
  variant_id: string;
  creative_id: string | null;
  variant_name: string;
  is_control: boolean;
  
  // Traffic
  traffic_percentage: number;
  
  // Metrics
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  revenue: number;
  
  // Calculated metrics
  ctr: number;
  cvr: number;
  roas: number;
  cpa: number;
  metric_value: number;
  
  // Statistical analysis
  confidence_interval: [number, number];
  is_leader: boolean;
  is_winner: boolean;
}

export interface StatisticalAnalysis {
  is_significant: boolean;
  p_value: number;
  winner_variant_id?: string;
  winner_name?: string;
  lift_percentage?: number;
  confidence?: number;
  test_statistic?: number;
  days_until_significant?: number;
  error?: string;
}

export interface ABTest {
  test_id: string;
  name: string;
  status: 'active' | 'completed' | 'stopped';
  goal_metric: 'ctr' | 'cvr' | 'roas' | 'cpa';
  confidence_level: number;
  traffic_split_method: 'equal' | 'weighted' | 'thompson';
  
  // Dates
  started_at: string | null;
  completed_at: string | null;
  days_running: number;
  
  // Variants
  variants: ABTestVariant[];
  
  // Results
  statistical_analysis: StatisticalAnalysis;
  
  // Optional
  campaign_id?: string;
  duration_days?: number;
}

export interface ABTestListItem {
  test_id: string;
  name: string;
  status: 'active' | 'completed' | 'stopped';
  goal_metric: string;
  confidence_level: number;
  started_at: string | null;
  completed_at: string | null;
  days_running: number;
  has_winner: boolean;
}

export interface CreateTestRequest {
  name: string;
  goal_metric: 'ctr' | 'cvr' | 'roas' | 'cpa';
  variants: VariantConfig[];
  campaign_id?: string;
  confidence_level?: number;
  traffic_split_method?: 'equal' | 'weighted' | 'thompson';
  duration_days?: number | null;
}

export interface VariantConfig {
  creative_id?: string;
  variant_name: string;
  traffic_percentage?: number;
}

export interface DeclareWinnerRequest {
  winner_variant_id: string;
}

export interface TestTemplate {
  id: string;
  name: string;
  description: string;
  goal_metric: 'ctr' | 'cvr' | 'roas' | 'cpa';
  confidence_level: number;
  recommended_duration_days: number;
  min_sample_size: number;
  example: string;
}

export interface SampleSizeCalculation {
  required_sample_size_per_variant: number;
  estimated_duration_days: number;
  baseline_value: number;
  minimum_detectable_effect: number;
  confidence_level: number;
  statistical_power: number;
  warnings: string[];
}

// Goal metric display helpers
export const GOAL_METRIC_LABELS: Record<string, string> = {
  ctr: 'Click-Through Rate (CTR)',
  cvr: 'Conversion Rate (CVR)',
  roas: 'Return on Ad Spend (ROAS)',
  cpa: 'Cost Per Acquisition (CPA)'
};

export const GOAL_METRIC_FORMATS: Record<string, (value: number) => string> = {
  ctr: (v) => `${(v * 100).toFixed(2)}%`,
  cvr: (v) => `${(v * 100).toFixed(2)}%`,
  roas: (v) => `${v.toFixed(2)}x`,
  cpa: (v) => `$${v.toFixed(2)}`
};

export const CONFIDENCE_LEVEL_OPTIONS = [
  { value: 0.90, label: '90% Confidence' },
  { value: 0.95, label: '95% Confidence (Recommended)' },
  { value: 0.99, label: '99% Confidence' }
];

export const TRAFFIC_SPLIT_OPTIONS = [
  { value: 'equal', label: 'Equal Split', description: 'Traffic divided evenly among variants' },
  { value: 'weighted', label: 'Manual Weights', description: 'You set traffic % per variant' },
  { value: 'thompson', label: 'Thompson Sampling', description: 'Auto-optimize toward winner' }
];
